/**
 * PriceSeries is a thin dispatcher that delegates to the appropriate
 * sub-component based on chart type and rendering mode:
 *
 * - Candlestick -> CandlestickSeries
 * - Line + showMultiColorLine -> MultiColorLine
 * - Line (default) -> SingleColorLine
 *
 * Owns the shared clip path and realtime dot rendering.
 */

import React, { useMemo, useRef } from "react";

import styles from "../styles/styles.module.css";
import { useChartContext } from "../core/ChartContext";
import { CandlestickSeries } from "./CandlestickSeries";
import { MultiColorLine } from "./MultiColorLine";
import { SingleColorLine } from "./SingleColorLine";
import {
  DEFAULT_CHART_COLOR,
  calculateRealtimeDotPosition,
  getChartLogger,
  getDirectionLineColor,
  getPointValue,
  getPriceDirection,
} from "~utils/ticker-chart";
import type { ChartType } from "~types/types";
import { useChartSubscription, useStoreRef } from "~hooks/ticker-chart";

export interface PriceSeriesProps {
  readonly seriesId: string;
  readonly chartType: ChartType;
  readonly color?: string;
  readonly strokeWidth?: number;
  readonly barWidth?: number;
  readonly spacing?: number;
  readonly showGradient?: boolean;
  readonly showRealtimeDot?: boolean;
  readonly showMultiColorLine?: boolean;
}

const PriceSeriesInner: React.FC<PriceSeriesProps> = ({
  seriesId,
  chartType,
  color,
  strokeWidth = 2,
  barWidth,
  spacing = 1,
  showGradient = false,
  showRealtimeDot = false,
  showMultiColorLine = false,
}) => {
  const logger = getChartLogger();
  const renderCountRef = useRef(0);

  renderCountRef.current += 1;
  logger.debug(
    `PriceSeries render #${renderCountRef.current}, seriesId: ${seriesId}`
  );

  const store = useChartContext();
  const storeRef = useStoreRef(store);

  const revision = useChartSubscription(store, ["data", "scales"]);
  const layout = storeRef.current.getLayout();

  // Calculate realtime dot position
  const realtimeDot = useMemo(() => {
    if (!showRealtimeDot || chartType !== "line") return null;

    const visibleData = storeRef.current.getVisibleData(seriesId);
    const scales = storeRef.current.getScales();

    return calculateRealtimeDotPosition({ visibleData, scales, layout });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, seriesId, chartType, showRealtimeDot]);

  const dotColor = useMemo(() => {
    if (!showRealtimeDot || chartType !== "line") return color;

    const state = storeRef.current.getState();

    if (state.mode === "comparison") {
      return color ?? DEFAULT_CHART_COLOR;
    }

    const visibleData = storeRef.current.getVisibleData(seriesId);
    const lastPoint = visibleData.at(-1);
    const lastValue = lastPoint ? getPointValue(lastPoint) : state.baseValue;
    const direction = getPriceDirection(state.baseValue, lastValue);

    return getDirectionLineColor(direction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, seriesId, chartType, showRealtimeDot, color]);

  // Dispatch to sub-component
  let content: React.ReactNode = null;

  if (chartType === "candlestick") {
    content = (
      <CandlestickSeries
        seriesId={seriesId}
        barWidth={barWidth}
        spacing={spacing}
      />
    );
  } else if (chartType === "line" && showMultiColorLine) {
    content = (
      <MultiColorLine
        seriesId={seriesId}
        strokeWidth={strokeWidth}
        showGradient={showGradient}
      />
    );
  } else if (chartType === "line") {
    content = (
      <SingleColorLine
        seriesId={seriesId}
        color={color}
        strokeWidth={strokeWidth}
        showGradient={showGradient}
      />
    );
  }

  return (
    <g
      className={styles.priceSeries}
      clipPath={`url(#clip-price-area-${seriesId})`}
      data-component="price-movement-chart-series"
    >
      <defs>
        <clipPath id={`clip-price-area-${seriesId}`}>
          <rect
            x={layout.priceArea.x}
            y={layout.priceArea.y}
            width={layout.priceArea.width}
            height={layout.priceArea.height}
          />
        </clipPath>
      </defs>

      {content}

      {realtimeDot && (
        <g
          className={styles.realtimeDotGroup}
          data-component="price-movement-chart-series-realtime-dot"
        >
          <circle
            className={styles.realtimePulseRing}
            cx={realtimeDot.x}
            cy={realtimeDot.y}
            r={5}
            fill={dotColor}
          />
          <circle
            className={styles.realtimeDot}
            cx={realtimeDot.x}
            cy={realtimeDot.y}
            r={4}
            fill={dotColor}
          />
        </g>
      )}
    </g>
  );
};

export const PriceSeries = React.memo(PriceSeriesInner);

PriceSeries.displayName = "PriceSeries";
